import {Client} from '@stomp/stompjs';
import store from '@store';
import {debounce} from '../utils';
import clientId from '@services/clientId';

/*
 * Canal WebSocket générique (STOMP) pour les évènements live poussés par le BO : threads aujourd'hui, d'autres
 * sections plus tard sans nouveau service — juste un nouveau `destination`. Une seule connexion partagée par toute
 * l'appli, réutilisée par tout service domaine qui appelle subscribe()/publish() (ex: threadRealtimeService).
 *
 * stompjs ne met PAS en file d'attente les subscribe() faits avant connexion (il lève une erreur) : ce module tient
 * donc lui-même le registre des abonnements actifs et les (ré)établit à chaque connexion/reconnexion.
 */

let client = null;
let nextId = 0;
const registry = new Map();

// Reconnexion (cf. ADR 0003 décision 5) : le broker ne rejoue rien de ce qui a été émis pendant une coupure, donc
// les consommateurs doivent relire leur état à la reconnexion. `hasConnectedOnce` évite de déclencher ce signal sur
// la connexion initiale (déjà couverte par le chargement normal des écrans). L'anti-rebond absorbe les rafales
// connect/disconnect d'un réseau instable en un seul signal.
let hasConnectedOnce = false;
const reconnectListeners = new Set();
const notifyReconnect = debounce(() => reconnectListeners.forEach((listener) => listener()), 2000);

function wsUrl() {
  // Le endpoint STOMP est enregistré à la racine du contexte ("/ws"), pas sous "/rest/v1" comme le reste de l'API.
  const base = import.meta.env.VUE_APP_BASE_API_URL.replace(/\/rest\/v1\/?$/, '');
  return base.replace(/^http/, 'ws') + '/ws';
}

function connectHeaders() {
  const user = store.state.currentUser;
  const headers = {'X-Client-Id': clientId};
  if (user && user.jwt) headers.Authorization = 'token=' + user.jwt;
  return headers;
}

function resubscribeAll() {
  registry.forEach((entry) => {
    entry.stompSubscription = client.subscribe(entry.destination, (message) => entry.onEvent(JSON.parse(message.body)));
  });
  if (hasConnectedOnce) {
    notifyReconnect();
  } else {
    hasConnectedOnce = true;
  }
}

function ensureClient() {
  if (client) return client;
  client = new Client({
    brokerURL: wsUrl(),
    reconnectDelay: 5000,
    // Relu à chaque (re)connexion : suit un token rafraîchi entre deux coupures de connexion.
    beforeConnect: () => {
      client.connectHeaders = connectHeaders();
    },
    onConnect: resubscribeAll,
    // Trame STOMP ERROR renvoyée par WebSocketAuthChannelInterceptor quand le CONNECT n'est pas authentifié
    // (typiquement : token expiré pendant que l'onglet reste ouvert sur la messagerie). Sans ce handler, stompjs
    // retente une connexion toutes les 5s avec le même token expiré, indéfiniment : comme l'intercepteur axios sur un
    // 401 REST, on rafraîchit le token (relu par beforeConnect à la reconnexion) et on déconnecte si le refresh échoue.
    onStompError: (frame) => {
      if (!frame.headers['X-Auth-Error'] || !store.getters['loggedIn']) return;
      store.dispatch('refresh').catch(() => store.dispatch('logOutExpired'));
    },
  });
  client.activate();
  return client;
}

export default {
  clientId,
  subscribe(destination, onEvent) {
    const c = ensureClient();
    const id = nextId++;
    const entry = {destination, onEvent, stompSubscription: null};
    registry.set(id, entry);
    if (c.connected) {
      entry.stompSubscription = c.subscribe(destination, (message) => onEvent(JSON.parse(message.body)));
    }
    return {
      unsubscribe() {
        const current = registry.get(id);
        if (current?.stompSubscription) current.stompSubscription.unsubscribe();
        registry.delete(id);
      },
    };
  },
  publish(destination, body) {
    const c = ensureClient();
    if (!c.connected) return;
    c.publish({destination, body: JSON.stringify(body)});
  },
  /**
   * Prévient d'une reconnexion (jamais de la connexion initiale) afin qu'un consommateur puisse relire ce qu'il a pu
   * manquer pendant la coupure -- le broker ne rejoue rien. Anti-rebond de 2s : un réseau instable ne déclenche
   * qu'un seul signal, pas une rafale. Renvoie une fonction de désinscription.
   */
  onReconnect(listener) {
    reconnectListeners.add(listener);
    return () => reconnectListeners.delete(listener);
  },
  /**
   * À appeler à la déconnexion : sans ça, les abonnements du compte qui se déconnecte restent en registre et sont
   * rejoués (resubscribeAll) sous le token du compte suivant qui se connecte dans le même onglet, y compris vers des
   * destinations que ce nouveau compte n'a pas le droit de lire (ex: /topic/threads pour un owner).
   */
  reset() {
    registry.clear();
    if (client) {
      client.deactivate();
      client = null;
    }
    hasConnectedOnce = false;
  },
};
